import React, { useState } from 'react';
import { Shield, Key, Eye, EyeOff, Moon, Sun, Smartphone } from 'lucide-react';

const Login = ({
    loginData,
    setLoginData,
    handleLogin,
    errorLogin,
    darkMode,
    toggleDarkMode,
    handlePasswordReset,
    mensajeReset,
    mfaState,
    handleMfaVerify,
    cancelMfa
}) => {
    const [mostrarPassword, setMostrarPassword] = useState(false);
    const [resetMode, setResetMode] = useState(false);
    const [mfaCode, setMfaCode] = useState('');

    const onSubmitMfa = (e) => {
        e.preventDefault();
        if (handleMfaVerify) {
            handleMfaVerify(mfaCode);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-4 transition-colors duration-300">
            {toggleDarkMode && (
                <button
                    onClick={toggleDarkMode}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition"
                    title={darkMode ? 'Modo claro' : 'Modo oscuro'}
                >
                    {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
            )}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8 w-full max-w-md transition-colors duration-300">
                <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-primary-100 dark:bg-primary-900 rounded-full mb-4">
                        <Shield className="text-primary-600 dark:text-primary-400" size={40} />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 leading-tight">
                        Sistema de Avisos y Alertas
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
                        {mfaState?.required 
                            ? 'Verificación en Dos Pasos (2FA - ENS)' 
                            : resetMode 
                                ? 'Restablecer contraseña' 
                                : 'Acceso seguro al sistema'}
                    </p>
                </div>

                {/* CASO 1: PANTALLA DE VERIFICACIÓN 2FA / MFA */}
                {mfaState?.required ? (
                    <form onSubmit={onSubmitMfa} className="space-y-4">
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                            <Smartphone size={20} className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
                            <div>
                                <p className="font-semibold">Autenticación en Dos Pasos Requerida</p>
                                <p className="text-xs mt-1">Introduce el código de 6 dígitos generado por tu aplicación de autenticación (Google Authenticator / Authy).</p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                                Código de Verificación (6 dígitos)
                            </label>
                            <input
                                type="text"
                                maxLength={6}
                                pattern="[0-9]{6}"
                                inputMode="numeric"
                                autoFocus
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                                className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono border-2 border-primary-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="000000"
                                required
                            />
                        </div>

                        {errorLogin && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                                {errorLogin}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={mfaCode.length !== 6}
                            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <Key size={20} />
                            Verificar y Acceder
                        </button>

                        <div className="text-center mt-4">
                            <button
                                type="button"
                                onClick={cancelMfa}
                                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition"
                            >
                                Cancelar y volver al inicio
                            </button>
                        </div>
                    </form>
                ) : !resetMode ? (
                    /* CASO 2: LOGIN ESTÁNDAR */
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                            <input
                                type="email"
                                value={loginData.email}
                                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contraseña</label>
                            <div className="relative">
                                <input
                                    type={mostrarPassword ? "text" : "password"}
                                    value={loginData.password}
                                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onMouseDown={() => setMostrarPassword(true)}
                                    onMouseUp={() => setMostrarPassword(false)}
                                    onMouseLeave={() => setMostrarPassword(false)}
                                    onTouchStart={() => setMostrarPassword(true)}
                                    onTouchEnd={() => setMostrarPassword(false)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                >
                                    {mostrarPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            <div className="mt-2 text-right">
                                <button 
                                    type="button"
                                    onClick={() => setResetMode(true)} 
                                    className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 font-semibold transition"
                                >
                                    ¿Has olvidado tu contraseña?
                                </button>
                            </div>
                        </div>

                        {errorLogin && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                                {errorLogin}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            <Key size={20} />
                            Iniciar Sesión
                        </button>
                    </form>
                ) : (
                    /* CASO 3: SOLICITUD DE RESET DE PASSWORD SEGURO */
                    <form onSubmit={(e) => { e.preventDefault(); handlePasswordReset(loginData.email); }} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                            <input
                                type="email"
                                value={loginData.email}
                                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="tu@email.es"
                                required
                            />
                        </div>

                        {mensajeReset && (
                            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm leading-relaxed">
                                {mensajeReset}
                            </div>
                        )}
                        {errorLogin && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                                {errorLogin}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Enviar enlace de recuperación
                        </button>

                        <div className="text-center mt-4">
                            <button 
                                type="button" 
                                onClick={() => setResetMode(false)} 
                                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition"
                            >
                                Volver al inicio de sesión
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;